import React, { useState, useEffect } from 'react';
import { Save, Settings as SettingsIcon, Clock, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTeamStore } from '../store/teamStore';
import { CoachInvitation } from './CoachInvitation';
import PageHeader from './layout/PageHeader';
import { useSettings } from '../hooks/useSettings';
import { TeamInformationSection } from './settings/TeamInformationSection';
import { PracticePreferencesSection } from './settings/PracticePreferencesSection';
import { DisplaySettingsSection } from './settings/DisplaySettingsSection';
import { DrillAlertSettingsSection } from './settings/DrillAlertSettingsSection';
import { FeatureSettingsSection } from './settings/FeatureSettingsSection';
import { TeamDeletionSection } from './settings/TeamDeletionSection';
import { CollapsibleSection } from './settings/CollapsibleSection';
import { SubscriptionCard } from './SubscriptionCard';

const Settings: React.FC = () => {
  const { allTeams, fetchAllTeams } = useTeamStore();
  const [isLoadingTeams, setIsLoadingTeams] = useState(true);

  const {
    currentTeam,
    formState,
    isLoading,
    isUpdating,
    lastSaved,
    forceUpdate,
    handleInputChange,
    handleTimeFormatChange,
    handlePlayerSortChange,
    handleToggleChange,
    handleSubmit
  } = useSettings();

  // Load teams on component mount
  useEffect(() => {
    const loadTeams = async () => {
      setIsLoadingTeams(true);
      await fetchAllTeams();
      setIsLoadingTeams(false);
    };

    loadTeams();
  }, [fetchAllTeams]);

  // Format last saved time
  const formatLastSaved = (date: Date | null) => {
    if (!date) return '';
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    
    if (diffSeconds < 30) return 'just now';
    if (diffSeconds < 60) return `${diffSeconds}s ago`;
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Show loading state while teams are being loaded
  if (isLoadingTeams) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mb-4"></div>
        <p className="caption">Loading teams...</p>
      </div>
    );
  }

  // Show team setup message only after teams are loaded and there are no teams
  if (allTeams.length === 0) {
    return (
      <div className="text-center">
        <h2 className="text-h3 mb-4">No Teams Found</h2>
        <p className="mb-6 caption">Get started by setting up your team.</p>
        <Link
          to="/team-setup"
          className="btn-primary"
        >
          Set Up Team
        </Link>
      </div>
    );
  }

  // Show loading state for team data
  if (!currentTeam) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mb-4"></div>
        <p className="caption">Loading team data...</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-600 mb-4"></div>
        <p className="text-gray-600">Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-4">
      <PageHeader 
        title="Settings" 
        icon={<SettingsIcon className="h-6 w-6" />}
        actions={
          <div className="flex items-center gap-2 text-sm text-gray-500">
            {isUpdating ? (
              <>
                <Clock className="w-4 h-4 animate-pulse" />
                <span>Saving...</span>
              </>
            ) : lastSaved ? (
              <>
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>Saved {formatLastSaved(lastSaved)}</span>
              </>
            ) : null}
          </div>
        }
      />
      
      <div className="max-w-7xl mx-auto pb-2 space-y-8">
        {/* Main Settings */}
        <div className="space-y-4">
          {/* Auto-save Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <Save className="w-4 h-4 text-blue-600" />
              <span className="text-blue-800 text-sm font-medium">
                Changes are automatically saved
              </span>
            </div>
            <p className="text-blue-600 text-xs mt-1">
              All settings are saved automatically as you make changes. No need to click save!
            </p>
          </div>

          {/* Team Information Section */}
          <CollapsibleSection title="Team Information" defaultExpanded={true}>
            <TeamInformationSection
              formState={formState}
              onInputChange={handleInputChange}
              onSubmit={handleSubmit}
            />
          </CollapsibleSection>

          {/* Practice Preferences Section */}
          <CollapsibleSection title="Practice Preferences" defaultExpanded={false}>
            <PracticePreferencesSection
              formState={formState}
              onInputChange={handleInputChange}
            />
          </CollapsibleSection>

          {/* Display Settings */}
          <CollapsibleSection title="Display Settings" defaultExpanded={false}>
            <DisplaySettingsSection
              formState={formState}
              onTimeFormatChange={handleTimeFormatChange}
              onPlayerSortChange={handlePlayerSortChange}
            />
          </CollapsibleSection>

          {/* Feature Settings */}
          <CollapsibleSection title="Feature Settings" defaultExpanded={false}>
            <FeatureSettingsSection
              formState={formState}
              sport={currentTeam.sport}
              onToggleChange={handleToggleChange}
            />
          </CollapsibleSection>

          {/* Drill Alert Settings */}
          <CollapsibleSection title="Drill Alert Settings" defaultExpanded={false}>
            <DrillAlertSettingsSection
              forceUpdate={forceUpdate}
            />
          </CollapsibleSection>

          {/* Coach Management Section */}
          <CollapsibleSection title="Coach Management" defaultExpanded={false}>
            <CoachInvitation />
          </CollapsibleSection>

          {/* Team Deletion Section */}
          <CollapsibleSection title="Danger Zone" defaultExpanded={false}>
            <TeamDeletionSection
              teamName={currentTeam.name}
              teamId={currentTeam.id}
            />
          </CollapsibleSection>
        </div>

        {/* Subscription at bottom of Settings – same UI as UpgradeModal */}
        <div className="max-w-2xl mx-auto">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Subscription</h2>
          <SubscriptionCard />
        </div>
      </div>
    </div>
  );
};

export default Settings; 